import React, { useState, useEffect } from 'react'
import Helmet from 'react-helmet'

import { isEqual } from 'lodash'
import { ChevronDownIcon, Download, Loader2, SlidersHorizontal } from 'lucide-react'
import moment from 'moment'
import queryString from 'query-string'
import { Badge, Button, FormGroup, Input, Label, DefaultPopover, Separator, DefaultTooltip, DropdownMenuItem } from 'twyne-new.ui';

import { DEFAULT_COLUMNS_CONFIG, applyColumnsConfig, reorderColumnsConfig, togglePinColumn } from '@Components/ColumnsConfig/config'
import { useGlobalStore } from '@Stores/global'
import appStore from 'stores/app'
import history from 'utils/history'

import DefaultTableResize from '../DefaultTableResize'
import InfiniteScroll from '../Grid/components/Table/InfiniteScroll'
import { useAppLayoutContext } from '../app-layout/context'
import { formatters } from '../hierarchical-report/utils'
import PermanentLink from '../reports/PermanentLink'
import { ReportBody } from '../reports/body'
import { ReportMenuExpandable, ReportMobileMenu } from '../reports/header'

import Configure from './Configure'
import Header from './Header'

const MAX_ROWS = 1000

export default class Drilldown extends React.Component {
  /**
   * OVERRITE ATTRIBUTES
   * @property {store: singletonStore} - The default api store
   * @property {collection: string} - The name of the elastic collection
   * @property {report: string} - The name of the report
   * @property {fields: { default: array, options: array }} - The summary cards data
   * @property {ignoreCustomFields: boolean} - If true, ignore custom fields
   */

  state = {
    title: '',
    metric: null,
    isLoading: false,

    selectedFields: null,
    columnsConfig: DEFAULT_COLUMNS_CONFIG,
    configureOpen: false,
    configureTab: 'fields',
    fields: [],
    rows: [],
    count: 0,

    options: {
      page: 1,
      size: 25,
    },
    filters: {
      filterby: {},
      startDate: null,
      endDate: null,
    },
  }

  get columns() {
    const { state: { selectedFields }, fields: { default: _default, fieldsFormatters } } = this

    const fields = selectedFields || _default
    const columns = fields.map(value => ({ dataField: value, text: formatters.groupsText(value), formatter: fieldsFormatters && fieldsFormatters[value] }))

    return applyColumnsConfig(columns, this.state.columnsConfig)
  }

  get columnsCatalog() {
    return this.state.fields.map(({ value }) => ({ key: value }))
  }

  handleChangeColumnsConfig = columnsConfig => {
    this.setState({ columnsConfig })
  }

  handleOpenColumnsConfig = () => {
    this.setState({ configureOpen: true, configureTab: 'columns' })
  }

  handleTogglePinColumn = column => {
    this.handleChangeColumnsConfig(togglePinColumn(this.state.columnsConfig, this.columnsCatalog, column.dataField))
  }

  handleRemoveColumn = column => {
    this.setState(({ selectedFields }) => ({ selectedFields: (selectedFields || []).filter(current => current !== column.dataField) }), this.loadData)
  }

  handleReorderColumns = (activeKey, overKey) => {
    const next = reorderColumnsConfig(this.state.columnsConfig, this.columnsCatalog, activeKey, overKey)
    if (next) {
      this.handleChangeColumnsConfig(next)
    }
  }

  get hasNextPage() {
    const { count, options: { size } } = this.state
    return count > size && size < MAX_ROWS
  }

  loadData = async () => {
    const { state: { filters, options, selectedFields } } = this
    const config = { ...filters, ...options, fields: selectedFields }
    this.setState({ isLoading: true })

    const { rows, count } = await this.store.drilldown(config)
    this.setState({ rows, count, isLoading: false })
  }

  dumpPermanentLink = () => {
    const { filters: { filterby, startDate, endDate }, metric, selectedFields, columnsConfig } = this.state
    return { filters: filterby, startDate: startDate?.format(), endDate: endDate?.format(), metric, selectedFields, columnsConfig }
  }

  exportData = () => {
    const { filters, selectedFields: fields, credential } = this.state
    return this.store.drilldown({ fields, isExport: true, ...filters, credential })
  }

  ExportForm = ({ onExport, loading }) => <>
    <FormGroup>
      <Label>Zip Password</Label>
      <Input required name="credential" value={this.state.credential}
        onKeyDown={e => e.stopPropagation()}
        onChange={e => this.setState({ credential: e.target.value })} />
    </FormGroup>
    <Button className='mt-2' color="accent" block onClick={onExport} disabled={loading}>
      {loading ? <Loader2 className='animate-spin' size='16' /> : 'Export'}
    </Button>
  </>

  applyPermanentLink = ({ filters, startDate, endDate, metric, selectedFields, columnsConfig }) => {
    const { field, value, label } = this.metrics[metric]
    if (field && !Object.hasOwn(filters, field)) {
      filters[field] = Array.isArray(value) ? value : [value]
    }

    const parseDate = date => moment.tz(moment.parseZone(date).format('YYYY-MM-DD HH:mm:ss'), appStore.timezone)

    this.setState(state => ({
      filters: { filterby: filters, startDate: parseDate(startDate), endDate: parseDate(endDate) },
      title: `Drilldown: ${label}`,
      metric,
      selectedFields: selectedFields || state.selectedFields,
      columnsConfig: columnsConfig || state.columnsConfig,
    }), () => this.state.fields.length && this.loadData())
  }

  PermanentLink = () => <PermanentLink dumpState={this.dumpPermanentLink} onLoad={this.applyPermanentLink} onError={() => history.push('/reports')} />

  Export = () => {
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const [show, setShow] = useState(false)
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(null)

    const handleExport = () => {
      setLoading(true)

      this.exportData().then(data => {
        setLoading(false)
        setSuccess(true)
      }).finally(() => {
        setTooltipOpen(true)
        setShow(false)
      })

      setTimeout(() => {
        setTooltipOpen(false)
      }, 3000)
    }

    return <>
      <DefaultPopover
        wrapperProps={{ open: show, onOpenChange: setShow }}
        content
        trigger={<>
          <DefaultTooltip side='left' open={tooltipOpen} content={tooltipOpen && success ? "The file is being processed and will be sent to your email shortly. Please, check your email inbox." : "Something went wrong. Please, try again later."}>
            <Button variant='outline' size='lg'>
              {loading ? <Loader2 className='animate-spin' size='16' /> : <>
                Export
                <ChevronDownIcon size='16' />
              </>}
            </Button>
          </DefaultTooltip>
        </>}
      >
        <span className='text-md'>Export File</span>
        <Separator className='m-0 my-2' />
        <this.ExportForm onExport={handleExport} />
      </DefaultPopover>
    </>
  }

  FiltersBadges = () => {
    const { filterby } = this.state.filters
    const filters = Object.entries(filterby)

    return <div className='flex gap-2 max-md:flex-wrap'>
      {filters.map(([key, value], index) => {
        value = value.join(', ')

        if ([null, undefined, ''].includes(value)) {
          value = 'empty'
        }

        if (['hour', 'day', 'month'].includes(key)) {
          value = value.split(', ')[0]
          value = moment.utc(value).tz(appStore.timezone).format(dateFormat[key])
        }

        return <Badge key={index} size='md' color='accent' variant='light'>{`${key}: ${value}`}</Badge>
      })}
    </div>
  }

  MobileMenu = () => {
    const [exportOpen, setExportOpen] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [message, setMessage] = useState(null)

    const handleExport = () => {
      setExporting(true)
      this.exportData()
        .then(() => setMessage('The file is being processed and will be sent to your email shortly.'))
        .catch(() => setMessage('Something went wrong. Please, try again later.'))
        .finally(() => setExporting(false))
    }

    return <ReportMobileMenu onOpenChange={open => {
      if (open) return
      setExportOpen(false)
      setMessage(null)
    }}>
      <DropdownMenuItem icon={SlidersHorizontal} onSelect={() => this.setState({ configureOpen: true, configureTab: 'fields' })}>Configure</DropdownMenuItem>
      <ReportMenuExpandable icon={Download} label='Export' open={exportOpen} onToggle={() => setExportOpen(open => !open)}>
        <div className='px-2.5 pt-2 pb-2'>
          <this.ExportForm onExport={handleExport} loading={exporting} />
          {message ? <small className='block mt-2 twy-text-base whitespace-normal'>{message}</small> : null}
        </div>
      </ReportMenuExpandable>
    </ReportMobileMenu>
  }

  Actions = () => <>
    <div className="flex justify-between items-center p-4 border-b twy-border-base">
      <div className='flex items-center gap-2 max-md:flex-wrap'>
        <strong>Filtered by:</strong>
        <this.FiltersBadges />
      </div>
      <div className='flex items-center gap-2 max-md:hidden'>
        <this.PermanentLink />
        <this.Configure />
        <this.Export />
      </div>
    </div>
  </>

  HeaderWrapper = () => {
    const { collection } = this
    const { title, filters: { startDate, endDate }, count, rows } = this.state

    return <Header
      isLoading={this.state.isLoading}
      title={title}
      collection={collection}
      startDate={startDate}
      endDate={endDate}
      total={count}
      showing={rows.length}
      mobileActions={<this.MobileMenu />}
    />
  }

  Configure = () => {
    const { selectedFields, fields, columnsConfig, configureOpen, configureTab } = this.state

    const handleApply = (nextFields, nextColumnsConfig) => {
      const reload = !isEqual(nextFields, this.state.selectedFields)
      this.setState({ selectedFields: nextFields, columnsConfig: nextColumnsConfig, configureOpen: false }, reload ? this.loadData : undefined)
    }

    return <>
      {configureOpen ? <Configure
        fields={selectedFields}
        allFields={fields}
        columnsConfig={columnsConfig}
        defaultTab={configureTab}
        onApply={handleApply}
        onCancel={() => this.setState({ configureOpen: false })}
      /> : <>
        <Button variant='outline' size="lg" onClick={() => this.setState({ configureOpen: true, configureTab: 'fields' })}>
          Configure
        </Button>
      </>}
    </>
  }

  Table = () => {
    const { columns } = this

    return <DefaultTableResize
      columns={columns}
      data={this.state.rows}
      onTogglePinColumn={this.handleTogglePinColumn}
      onRemoveColumn={this.handleRemoveColumn}
      onReorderColumns={this.handleReorderColumns}
      onOpenColumnsConfig={this.handleOpenColumnsConfig}
    />
  }

  Scroll = () => {
    if (!this.hasNextPage) {
      return null
    }

    const handleNextPage = () => {
      if (this.state.isLoading) {
        return
      }

      this.setState(({ options }) => {
        options.size += options.size
        return { options }
      }, this.loadData)
    }

    return <div className="flex w-full justify-center items-center h-14">
      <InfiniteScroll onVisit={handleNextPage} hasMore={this.hasNextPage} />
    </div>
  }

  Content = () => {
    const { customFields: { getFields }, metaFields: { getFields: getMetaFields } } = useGlobalStore()
    const { fields: { options, default: selectedFields }, state: { fields } } = this
    const { setMenuCollapsed } = useAppLayoutContext()
    const [hasConfigure] = useState(() => !!queryString.parse(history.location.search).configure)

    useEffect(() => {
      setMenuCollapsed(true)
    }, [])

    const loadCustomFields = async () => {
      const metaFields = await getMetaFields()

      const metaFieldsOptions = []
      metaFields.forEach(({ key: value }) => {
        metaFieldsOptions.push({ value, label: value, group: "Meta Fields" })
      })

      if (this.ignoreCustomFields) {
        this.setState({ fields: options.concat(metaFieldsOptions) })
        return
      }

      const customFields = await getFields()

      const customFieldsOptions = []
      customFields.forEach(({ key, name }) => {
        customFieldsOptions.push(
          { value: `cf_${key}`, label: name, group: "Custom Fields" },
          { value: `cf_${key}_trustedform`, label: name, group: "Custom Fields (Trustedform)" },
          { value: `cf_${key}_leadid`, label: name, group: "Custom Fields (Leadid)" },
        )
      })

      this.setState({
        fields: options.concat(customFieldsOptions).concat(metaFieldsOptions),
      })
    }

    useEffect(() => {
      if (!hasConfigure) return history.push('/reports')
      loadCustomFields()
      this.setState(state => ({ selectedFields: state.selectedFields || selectedFields }))
    }, [])

    useEffect(() => {
      if (fields.length && this.state.filters.startDate) {
        this.loadData()
      }
    }, [fields])

    return <>
      <Helmet title={this.state.title} />
      <this.HeaderWrapper />
      <this.Actions />
      <ReportBody className='max-h-[calc(100vh-195px)]' loading={this.state.isLoading}>
        <this.Table />
        <this.Scroll />
      </ReportBody>
    </>
  }

  render = () => <this.Content />
}

const dateFormat = { hour: 'MM/DD/YYYY [at] hh:mm A', day: 'MM/DD/YYYY', month: 'MM/YYYY' }
